import React, { createContext, useContext, useEffect, useMemo, useReducer } from "react";
import { toast } from "sonner";
import { fbqTrack } from "@/lib/pixel";

const CartContext = createContext(null);
const KEY = "dd_cart_v1";

function reducer(state, action) {
  switch (action.type) {
    case "INIT":
      return action.payload;
    case "ADD": {
      const { product, qty } = action;
      const existing = state.find((i) => i.product_id === product.id);
      if (existing) {
        return state.map((i) =>
          i.product_id === product.id ? { ...i, quantity: i.quantity + qty } : i
        );
      }
      return [
        ...state,
        {
          product_id: product.id,
          name: product.name,
          price: product.price,
          image: product.image,
          scent_label: product.scent_label,
          volume: product.volume,
          quantity: qty,
        },
      ];
    }
    case "SET_QTY":
      return state
        .map((i) => (i.product_id === action.id ? { ...i, quantity: action.qty } : i))
        .filter((i) => i.quantity > 0);
    case "REMOVE":
      return state.filter((i) => i.product_id !== action.id);
    case "CLEAR":
      return [];
    default:
      return state;
  }
}

export function CartProvider({ children }) {
  const [items, dispatch] = useReducer(reducer, []);
  const [open, setOpen] = React.useState(false);
  const [pendingCheckout, setPendingCheckout] = React.useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(KEY);
      if (raw) dispatch({ type: "INIT", payload: JSON.parse(raw) });
    } catch (e) {}
  }, []);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  const addItem = (product, qty = 1) => {
    dispatch({ type: "ADD", product, qty });
    toast.success(`${product.name} adăugat în coș`, { description: product.scent_label });
    setOpen(true);
    fbqTrack("AddToCart", {
      content_ids: [product.id],
      content_name: product.name,
      content_type: "product",
      value: Number((product.price * qty).toFixed(2)),
      currency: "RON",
    });
  };

  const orderNow = (product, qty = 1) => {
    dispatch({ type: "ADD", product, qty });
    setOpen(true);
    setPendingCheckout(true);
    fbqTrack("AddToCart", {
      content_ids: [product.id],
      content_name: product.name,
      content_type: "product",
      value: Number((product.price * qty).toFixed(2)),
      currency: "RON",
    });
  };

  const value = useMemo(() => {
    const count = items.reduce((s, i) => s + i.quantity, 0);
    const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
    const shipping = 0;
    return {
      items,
      open,
      setOpen,
      addItem,
      orderNow,
      pendingCheckout,
      setPendingCheckout,
      setQty: (id, qty) => dispatch({ type: "SET_QTY", id, qty }),
      remove: (id) => dispatch({ type: "REMOVE", id }),
      clear: () => dispatch({ type: "CLEAR" }),
      count,
      subtotal,
      shipping,
      total: subtotal + shipping,
    };
  }, [items, open, pendingCheckout]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
